/**
 * Authentication API endpoints
 */
import type {
    ApiResponse,
    AuthResponse,
    LoginRequest,
    LdapRequest,
    UserContext,
    UserMfaSetup,
} from "../lib/types";
import { apiRequest, authenticatedRequest } from "../lib/api";

export async function loginWithPassword(
    credentials: LoginRequest
): Promise<AuthResponse> {
    return apiRequest<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
    });
}

export async function loginWithLdap(
    credentials: LdapRequest
): Promise<AuthResponse> {
    return apiRequest<AuthResponse>("/auth/ldap", {
        method: "POST",
        body: JSON.stringify(credentials),
    });
}

export async function loginWithGoogle(
    code: string,
    redirectUri: string,
    companyAlias: string,
    app: "web" | "admin" = "web"
): Promise<AuthResponse> {
    return apiRequest<AuthResponse>("/auth/google", {
        method: "POST",
        body: JSON.stringify({ code, redirectUri, companyAlias, app }),
    });
}

export async function getCurrentUser(token: string): Promise<UserContext> {
    return authenticatedRequest<ApiResponse<UserContext>>("/api/me", token).then(
        (res) => res.data
    );
}

export async function verifyMFA(
    mfaToken: string,
    code: string
): Promise<AuthResponse> {
    return apiRequest<AuthResponse>("/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ mfaToken, code }),
    });
}

export async function setupMFA(token: string): Promise<UserMfaSetup> {
    return authenticatedRequest<UserMfaSetup>("/auth/mfa/setup", token);
}

export async function enableMFA(
    token: string,
    secret: string,
    code: string,
    app: "web" | "admin"
): Promise<{ success: boolean; token?: string }> {
    return authenticatedRequest<ApiResponse<{ success: boolean; token?: string }>>(
        "/auth/mfa/enable",
        token,
        {
            method: "POST",
            body: JSON.stringify({ secret, code, app }),
        }
    ).then((res) => res.data);
}

export async function disableMFA(token: string, app: "web" | "admin"): Promise<{ success: boolean }> {
    return authenticatedRequest<{ success: boolean }>("/auth/mfa/disable", token, {
        method: "POST",
        body: JSON.stringify({ app }),
    });
}

export async function changePassword(
    token: string,
    currentPassword: string,
    newPassword: string
): Promise<{ success: boolean }> {
    return authenticatedRequest<{ success: boolean }>("/auth/change-password", token, {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
    });
}
